<script setup lang="ts">
    import { useGlobalConfig } from '~/composables/storyblok-proxy/useGlobalConfig';
    import { useStories } from '~/composables/storyblok-proxy/useStories';
    import { getDefaultLanguageForCountry } from '~/composables/useCountryLanguages';
    import { normalizeCountryEntries } from '~/utils/countryConfig';
    import type { CountryListItem } from '~/utils/countryConfig';
    import type { ISbStoriesParams } from '@storyblok/vue';
    import type { LocalizedLinkListStoryblok } from '~storyblok-types/LocalizedLinkListStoryblok';

    type LinkedStoryReference = string | { uuid?: string; id?: string; [key: string]: unknown };
    type StoryTranslation = { lang?: string; path?: string | null; published?: boolean | null };
    type StoryMeta = {
        uuid?: string;
        full_slug?: string;
        default_full_slug?: string | null;
        translated_slugs?: StoryTranslation[];
    };

    const { blok } = defineProps<{ blok: LocalizedLinkListStoryblok & { headline?: string } }>();

    const { country, lang } = useLanguage();
    const countryLanguages = useCountryLanguages();
    const { isDraftModeEnabled } = useDraftMode();

    const currentCountryCode = ref(country.value);
    const currentLanguageCode = ref(lang.value);
    const currentLink = computed(() => {
        return localizedLinks.value.find(
            (link) => link.country === currentCountryCode.value && link.lang === currentLanguageCode.value
        )?.to;
    });

    // country config
    const { data: globalCountryConfigStory } = await useGlobalConfig<CountryConfigStoryblok>('globalcountryconfig', {
        cv: 0
    });
    const rawCountryEntries = computed<CountryEntryStoryblok[]>(() => {
        return globalCountryConfigStory.value?.content?.CountryList || [];
    });
    const countriesList = computed<CountryListItem[]>(() => normalizeCountryEntries(rawCountryEntries.value));

    // fetch story meta data
    const linkedStoryUuids = computed<string[]>(() => {
        return (blok.linkedStories || [])
            .map((entry: LinkedStoryReference) => {
                if (typeof entry === 'string') return entry;
                if (entry && typeof entry === 'object') {
                    if (typeof entry.uuid === 'string') return entry.uuid;
                    if (typeof entry.id === 'string') return entry.id;
                }
                return '';
            })
            .filter(Boolean);
    });
    const excludeFields = ['body', 'seo', 'customTabs', 'storyWrapper', 'AI_SEO'];
    const storyQueryParams = computed<ISbStoriesParams>(() => {
        return {
            by_uuids: linkedStoryUuids.value.join(','),
            per_page: linkedStoryUuids.value.length,
            excluding_fields: excludeFields.join(',')
        };
    });
    const { data: linkedStoriesData } = useStories<StoryMeta>(storyQueryParams, {
        disableCountrySegment: true
    });

    const localizedLinks = computed(() => {
        return (linkedStoriesData.value || []).flatMap((story) => {
            try {
                const storyCountry = getStoryCountry(story, countryLanguages);
                const languageConfig = countryLanguages[storyCountry];
                if (!languageConfig) return [];

                return languageConfig.availableLanguages.flatMap((language) => {
                    const to = resolveStoryPath(story, language, countryLanguages);
                    return to ? [{ to, lang: language, country: storyCountry }] : [];
                });
            } catch (error) {
                log.warn('[LocalizedLinkList] Could not resolve linked story', error);
                return [];
            }
        });
    });

    /**
     * Show only countries that have at least one localized link and only the languages that
     * have a localized link for that country
     */
    const filteredCountries = computed<CountryListItem[]>(() =>
        countriesList.value
            .filter((item) => localizedLinks.value.some((link) => link.country === item.key))
            .map((item) => ({
                ...item,
                languages: item.languages.filter((langOption) =>
                    localizedLinks.value.some(
                        (localLink) => langOption === localLink.lang && localLink.country === item.key
                    )
                )
            }))
    );

    const onSelectCountryLanguage = async (countryCode: string, languageCode: string) => {
        currentCountryCode.value = countryCode;
        currentLanguageCode.value = languageCode;
    };

    const onSelectCountryDefaultLanguage = (countryCode: string) => {
        currentCountryCode.value = countryCode;
        const defaultLang = getDefaultLanguageForCountry(countryCode);
        if (defaultLang) currentLanguageCode.value = defaultLang;
    };

    watch([country, lang], () => {
        currentCountryCode.value = country.value;
        currentLanguageCode.value = lang.value;
    });
</script>

<template>
    <section
        v-editable="blok"
        :aria-label="$t('localizedLinkList.ariaLabel')"
        class="wrapper"
    >
        <LocalizedLinkListAmbiguousLinksWarning
            v-if="isDraftModeEnabled"
            :localized-links="localizedLinks"
        />

        <div
            role="radiogroup"
            class="country-list"
        >
            <HeaderLanguagePickerCountryItem
                v-for="countryItem in filteredCountries"
                :key="countryItem.key"
                dropdown
                class="picker-item"
                :country="countryItem"
                :pending-country-code="currentCountryCode"
                :pending-language-code="currentLanguageCode"
                @select-country-default-language="onSelectCountryDefaultLanguage"
                @select-country-language="onSelectCountryLanguage"
            />
        </div>

        <PrimaryButton
            id="confirm-selection-button"
            :button-href="currentLink"
            :disabled="!currentLink"
            class="cta-button"
            :button-text="$t('localizedLinkList.gotoLocalizedLink')"
            retain-size
            size="small"
            alignment="center"
            has-icon
        />
    </section>
</template>

<style scoped lang="scss">
    .wrapper {
        margin: var(--spacing--2) auto;
        max-width: var(--max-width);
    }

    .country-list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: var(--spacing);
        margin: var(--spacing--2) auto;
        width: 100%;

        .picker-item {
            border: 2px solid var(--gray-light);
            border-radius: var(--border-radius);
            transition:
                border-color $anim-duration-hover-long $anim-ease,
                background-color $anim-duration-hover-long $anim-ease;
            &.--active,
            &:hover {
                border-color: var(--brand-primary);
            }
        }
    }

    .cta-button {
        margin: var(--spacing--2) auto 0;
    }
</style>
